import { GlobalState } from '@highlight/ipc'
import type { ChatAttachment } from '../types'

/**
 * Wait (10s max) for an in-flight assistant context request to finish before
 * submitting, so the turn picks up the context it was waiting on.
 */
export async function waitForAssistantContext(): Promise<void> {
  const isRequestingContext = GlobalState.get<boolean>('assistant.requestingContext', false)
  if (!isRequestingContext) return

  console.time('Wait for context')
  console.log('Waiting for context to finish requesting...')
  await new Promise<void>((resolve) => {
    const onRequested = () => {
      const requesting = GlobalState.get<boolean>('assistant.requestingContext', false)
      if (!requesting) {
        clearTimeout(timeout)
        GlobalState.off('assistant.requestingContext', onRequested)
        resolve()
        console.timeEnd('Wait for context')
      }
    }

    // Wait 10s max
    const timeout = setTimeout(() => {
      console.warn('Timed out waiting for context!')
      GlobalState.off('assistant.requestingContext', onRequested)
      resolve()
    }, 10000)

    // Wait for request to finish
    GlobalState.on('assistant.requestingContext', onRequested)
  })
}

type RequestedContext = {
  environment: { imageDataBase64?: string; ocrScreenContents?: string }
  application?: { name?: string; focusedWindow?: { title?: string } }
}

/**
 * Turn a freshly-requested window context into chat attachments (screenshot +
 * OCR text; onboarding falls back to a display screenshot). Returns undefined
 * when no context was captured — the caller keeps its existing attachments.
 */
export async function collectRequestedContextAttachments(
  requestCurrentContext: () => Promise<RequestedContext | null | undefined>,
  options: { isOnboardingChat?: boolean },
  getActiveDisplayScreenshot?: () => Promise<string | null | undefined>,
): Promise<Array<ChatAttachment> | undefined> {
  console.time('requestCurrentContext')
  const context = await requestCurrentContext()
  console.timeEnd('requestCurrentContext')

  // For visible scope, attach the screenshot of the window context
  const attachments: Array<ChatAttachment> = []
  const screenshot = context?.environment.imageDataBase64
  if (screenshot) {
    attachments.push({
      pending: {
        type: 'image',
        name: context?.application?.focusedWindow?.title ?? context?.application?.name ?? 'Screenshot',
        body: screenshot,
      },
    })
  }

  // If visible text exists, always attach it as "OCR text"
  const visibleOcrText = context?.environment.ocrScreenContents
  if (visibleOcrText) {
    attachments.push({
      attachment: {
        type: 'ocr',
        text: visibleOcrText,
      },
    })
  }

  if (options.isOnboardingChat && attachments.length === 0 && getActiveDisplayScreenshot) {
    const activeDisplayScreenshot = await getActiveDisplayScreenshot()
    if (activeDisplayScreenshot) {
      attachments.push({
        pending: {
          type: 'image',
          name: 'Screenshot',
          body: activeDisplayScreenshot,
        },
      })
    }
  }

  // The onboarding display-screenshot fallback can populate attachments even
  // when the context request itself came back empty — still ship those.
  return context || attachments.length > 0 ? attachments : undefined
}
